import React, { useEffect, useState } from 'react';
import './AsanaTasks.css';

const TEAMS = ['Design', 'DevOps', 'IE Front End', 'PTC Front End'];
const STAGES = ['Intake', 'Queue', 'Currently Working'];

const FIELD_LABELS = {
  name: 'Task Name',
  value_added: 'Value Added',
  points: 'Points',
  priority_level: 'Priority Level',
  stage_: 'Stage',
  team: 'Team',
  initiator_: 'Initiator',
  status: 'Status'
};

const DISPLAY_FIELDS = {
  DevOps: {
    Intake: ['name', 'initiator_'],
    Queue: ['name', 'initiator_', 'priority_level', 'value_added'],
    'Currently Working': ['name', 'status', 'initiator_', 'value_added'],
  },
  Design: {
    Intake: ['name', 'initiator_'],
    Queue: ['name', 'initiator_', 'priority_level', 'value_added'],
    'Currently Working': ['name', 'status', 'initiator_', 'value_added'],
  },
  'IE Front End': {
    Intake: ['name', 'initiator_'],
    Queue: ['name', 'initiator_', 'priority_level', 'value_added'],
    'Currently Working': ['name', 'status', 'initiator_', 'value_added'],
  },
  'PTC Front End': {
    Intake: ['name', 'initiator_'],
    Queue: ['name', 'initiator_', 'priority_level', 'value_added'],
    'Currently Working': ['name', 'status', 'initiator__', 'value_added'],
  },
};

// Define which field to sort by for each stage
const STAGE_SORT_FIELDS = {
  Intake: 'priority_level',
  Queue: 'priority_level',
  'Currently Working': 'status',
};

// Custom order maps for priority_level and status
const PRIORITY_ORDER = { 'Level 1': 1, 'Level 2': 2, 'Level 3': 3, 'N/A': 4 };
const STATUS_ORDER = { 'In Progress': 1, Complete: 2, 'Not Started': 3, 'N/A': 4 };

function AsanaTasks() {
  const [selectedTeam, setSelectedTeam] = useState('DevOps');
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState({});

  // Extract a specific custom field by name (e.g., "points" or "value")
  const extractCustomField = (fields, key) => {
    const match = fields.find((f) =>
      f.name.toLowerCase().includes(key.toLowerCase())
    );
    return match?.number_value ?? match?.text_value ?? 'N/A';
  };

  const extractTeam = (fields) => {
    const match = fields.find((f) => f.name.toLowerCase() === 'team');
    return match?.text_value || 'Unknown';
  };

  const extractStage = (fields) => {
    const match = fields.find((f) => f.name.toLowerCase() === 'stage_');
    return match?.text_value || 'Unknown';
  };

  useEffect(() => {
    const fetchAsanaTasks = async () => {
      const token = process.env.REACT_APP_ASANA_ACCESS_TOKEN;
      const projectId = process.env.REACT_APP_ASANA_PROJECT_ID;

      try {
        const response = await fetch(
          `https://app.asana.com/api/1.0/projects/${projectId}/tasks?opt_fields=name,custom_fields`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        const parsedTasks = data.data.map((task) => {
          const customFields = task.custom_fields || [];

          return {
            priority_level: extractCustomField(customFields, 'priority'),
            name: task.name,
            stage_: extractStage(customFields, 'stage_'),
            team: extractTeam(customFields, 'team'),
            value_added: extractCustomField(customFields, 'value'),
            status: extractCustomField(customFields, 'status'),
            initiator__: extractCustomField(customFields, 'initiator__'),
          };
        });

        console.log('Parsed tasks:', parsedTasks); // Debug
        setTasks(parsedTasks);
      } catch (error) {
        console.error('Error fetching Asana tasks:', error);
      }
    };

    fetchAsanaTasks();
    const interval = setInterval(
      fetchAsanaTasks,
      parseInt(process.env.REACT_APP_FETCH_INTERVAL, 10)
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;

    const stageMap = {
      Intake: [],
      Queue: [],
      'Currently Working': [],
    };

    tasks.forEach((task) => {
      if (task.team === selectedTeam && stageMap[task.stage_]) {
        stageMap[task.stage_].push(task);
      }
    });

    setFilteredTasks(stageMap);
  }, [selectedTeam, tasks]);

  return (
    <div>
      <label>
        Select Team:{' '}
        <select
          value={selectedTeam}
          onChange={(e) => setSelectedTeam(e.target.value)}
        >
          {TEAMS.map((team) => (
            <option key={team} value={team}>
              {team}
            </option>
          ))}
        </select>
      </label>

      {selectedTeam && (
        <div
          className="task-columns"
          style={{ display: 'flex', gap: '2rem', marginTop: '2rem' }}
        >
          {STAGES.map((stage_) => (
            <div key={stage_} style={{ flex: 1 }}>
              <h3>{stage_}</h3>
              {filteredTasks[stage_]?.length ? (
                <ul>
                  {[...filteredTasks[stage_]]
                    .sort((a, b) => {
                      const sortField = STAGE_SORT_FIELDS[stage_] || 'name';

                      let aVal = a[sortField] ?? 'N/A';
                      let bVal = b[sortField] ?? 'N/A';

                      if (sortField === 'priority_level') {
                        aVal = PRIORITY_ORDER[aVal] ?? 999;
                        bVal = PRIORITY_ORDER[bVal] ?? 999;
                      } else if (sortField === 'status') {
                        aVal = STATUS_ORDER[aVal] ?? 999;
                        bVal = STATUS_ORDER[bVal] ?? 999;
                      }

                      if (aVal < bVal) return -1;
                      if (aVal > bVal) return 1;
                      return 0;
                    })
                    .map((task, idx) => {
                      const fieldsToShow =
                        DISPLAY_FIELDS[selectedTeam]?.[stage_] || ['name'];

                      return (
                        <li key={idx}>
                          {fieldsToShow.map((field) => (
                            <span key={field} style={{ marginRight: '1rem' }}>
                              <strong>{FIELD_LABELS[field] || field}:</strong>{' '}
                              {task[field]}
                            </span>
                          ))}
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p>No tasks</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default AsanaTasks;
